import type { Message } from '@ag-ui/client'
import { isRecord } from './json'

export interface SidebarTaskItem {
  kind: 'task'
  id: string
  label: string
  /**
   * Defaults to showing as still running (the spinner row) when absent —
   * only sources that can actually report a terminal outcome (e.g. a
   * tracked Nexthink action) set this to 'success'/'failure'.
   */
  status?: 'pending' | 'success' | 'failure'
}

export interface SidebarTicketItem {
  kind: 'ticket'
  id: string
  status: string
  ticketNumber: string
  openedAt?: string
  description: string
}

export type SidebarItem = SidebarTaskItem | SidebarTicketItem

/**
 * Tool names are whatever the backend agent defines (there is no fixed enum
 * shared with the frontend), so a new request/incident/order/assistance tool
 * call is recognised by keyword rather than an exact name match.
 */
const TICKET_TOOL_NAME_PATTERN = /incident|ticket|request|order|assistance/i

export function humanizeToolName(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

function firstString(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim().length > 0) return value
  }
  return undefined
}

function mapTicketFields(
  record: Record<string, unknown>,
  ticketNumber: string,
  fallbackDescription: string,
): SidebarTicketItem {
  return {
    kind: 'ticket',
    id: ticketNumber,
    ticketNumber,
    status: firstString(record, ['status', 'state']) ?? 'Submitted',
    openedAt: firstString(record, ['openedAt', 'opened_at', 'createdAt', 'created_at']),
    description: firstString(record, ['short_description']) ?? fallbackDescription,
  }
}

/**
 * Returns `undefined` without a real ticket number, so the backend poll stays
 * the only source of that ticket's card (no placeholder-id duplicate).
 */
function toTicketItem(resultContent: string): SidebarTicketItem | undefined {
  const parsed = safeJsonParse(resultContent)
  const record = isRecord(parsed) ? parsed : {}
  const ticketNumber = firstString(record, ['number', 'ticketNumber', 'incidentNumber', 'id'])
  if (!ticketNumber) return undefined

  return mapTicketFields(
    record,
    ticketNumber,
    typeof parsed === 'string' ? parsed : resultContent,
  )
}

/**
 * Maps one `GET /service/incidents` row to a ticket item, keyed by ticket number.
 * Returns `undefined` for a row without one.
 */
export function incidentRecordToTicketItem(
  record: Record<string, unknown>,
): SidebarTicketItem | undefined {
  const ticketNumber = firstString(record, ['number', 'ticketNumber', 'id'])
  return ticketNumber ? mapTicketFields(record, ticketNumber, '') : undefined
}

function collectTicketToolCalls(messages: readonly Message[]) {
  const toolCallNameById = new Map<string, string>()
  const resultContentById = new Map<string, string>()

  for (const message of messages) {
    if (message.role === 'assistant' && message.toolCalls) {
      for (const call of message.toolCalls) {
        toolCallNameById.set(call.id, call.function.name)
      }
    }
    if (message.role === 'tool') {
      resultContentById.set(message.toolCallId, message.content)
    }
  }

  return { toolCallNameById, resultContentById }
}

/**
 * Derives the "In Progress" side panel's list from the raw AG-UI message
 * log: a tool call matching a ticket/incident/request/assistance tool name
 * that has not resulted yet is an in-progress task; once its `tool` result
 * message arrives, it becomes a ticket card. Newest first.
 */
export function buildTicketSidebarItems(
  messages: readonly Message[],
): SidebarItem[] {
  const { toolCallNameById, resultContentById } = collectTicketToolCalls(messages)

  const items: SidebarItem[] = []

  for (const [toolCallId, toolName] of toolCallNameById) {
    if (!TICKET_TOOL_NAME_PATTERN.test(toolName)) continue

    const resultContent = resultContentById.get(toolCallId)
    if (resultContent === undefined) {
      items.push({ kind: 'task', id: toolCallId, label: humanizeToolName(toolName) })
      continue
    }

    const ticketItem = toTicketItem(resultContent)
    if (ticketItem) items.push(ticketItem)
  }

  return items.reverse()
}
